/**
 * Storico audit (E7.6) di un'entità del portale, riusabile: pannello collassabile (chiuso di
 * default, salvo `defaultOpen`) che legge gli eventi solo all'apertura. Visibile solo con il permesso VIEW_AUDIT_LOG,
 * come la pagina Log Audit: gli eventi contengono gli attori delle modifiche.
 *
 * Il chiamante sceglie le serie da leggere (`queries`) e come descrivere un evento
 * (`describe`, etichette dei campi): qui solo fetch, toggle e resa "chi / quando / cosa".
 */

import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePermissions } from '../../auth/usePermissions.js';
import { fetchAuditHistory, type AuditEventRow, type AuditHistoryQuery } from './audit-api.js';
import styles from './AuditTrail.module.css';

export const AUDIT_ACTION_LABEL: Record<string, string> = {
  create: 'Creazione',
  modify: 'Modifica',
  delete: 'Eliminazione',
  restore: 'Ripristino',
  download: 'Download',
  inventory_upload: 'Caricamento BOM',
  access_denied: 'Accesso negato',
  auth_failure: 'Autenticazione fallita',
};

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

/** Valore leggibile di un campo prima/dopo (liste separate da virgola, oggetti in JSON). */
export function formatAuditValue(value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';
  if (Array.isArray(value)) return value.map((v) => formatAuditValue(v)).join(', ') || '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export interface AuditChangeRow {
  campo: string;
  label: string;
  /** Valore precedente, null se il campo non c'era prima. */
  prima: string | null;
  dopo: string;
  invariato: boolean;
}

/** Righe campo/prima/dopo di un evento; un campo uguale prima e dopo è solo contesto. */
export function auditChangeRows(
  ev: Pick<AuditEventRow, 'before' | 'after'>,
  fieldLabel: Record<string, string> = {},
): AuditChangeRow[] {
  const before = asRecord(ev.before);
  const after = asRecord(ev.after);
  const campi = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  return campi.map((campo) => {
    const prima = formatAuditValue(before[campo]);
    const dopo = formatAuditValue(after[campo]);
    return {
      campo,
      label: fieldLabel[campo] ?? campo,
      prima: campo in before ? prima : null,
      dopo,
      invariato: campo in before && prima === dopo,
    };
  });
}

export interface AuditTrailProps {
  /** Chiave TanStack Query (le mutazioni possono invalidarla per prefisso). */
  queryKey: readonly unknown[];
  /** Serie da leggere e unire (vedi fetchAuditHistory). */
  queries: AuditHistoryQuery[];
  /** Prefisso dei data-testid: `<testId>`, `<testId>-toggle`, `<testId>-lista`, … */
  testId: string;
  /** Titolo del toggle (default "Storico modifiche"). */
  title?: string;
  /** Descrizione dell'oggetto dell'evento (es. "Release 1.2.0"): accanto all'azione. */
  describe?: (ev: AuditEventRow) => ReactNode;
  /** Etichetta di un'azione; default {@link AUDIT_ACTION_LABEL}. */
  actionLabel?: (ev: AuditEventRow) => string;
  /** Etichette dei campi del dettaglio prima/dopo. */
  fieldLabel?: Record<string, string>;
  /** Pannello aperto al primo render (default chiuso). */
  defaultOpen?: boolean;
}

export function AuditTrail({
  queryKey,
  queries,
  testId,
  title = 'Storico modifiche',
  describe,
  actionLabel,
  fieldLabel,
  defaultOpen = false,
}: AuditTrailProps) {
  const { can } = usePermissions();
  const consentito = can('VIEW_AUDIT_LOG');
  const [aperto, setAperto] = useState(defaultOpen);
  const {
    data: eventi,
    isLoading,
    isError,
  } = useQuery({
    queryKey,
    queryFn: () => fetchAuditHistory(queries),
    enabled: aperto && consentito && queries.length > 0,
  });

  if (!consentito) return null;

  return (
    <section className={styles.trail} data-testid={testId}>
      <button
        type="button"
        className={styles.toggle}
        aria-expanded={aperto}
        onClick={(e) => {
          e.stopPropagation();
          setAperto((v) => !v);
        }}
        data-testid={`${testId}-toggle`}
      >
        <span className={styles.chevron} data-aperto={aperto} aria-hidden="true">
          ▸
        </span>
        {title}{' '}
        <span className={styles.muted}>
          (log audit{eventi ? ` — ${eventi.length} eventi` : ''})
        </span>
      </button>
      {aperto && (
        <div className={styles.body} data-testid={`${testId}-pannello`}>
          {isLoading && <p className={styles.muted}>Caricamento storico…</p>}
          {isError && (
            <p className={styles.error} data-testid={`${testId}-errore`}>
              Impossibile caricare lo storico delle modifiche.
            </p>
          )}
          {eventi && eventi.length === 0 && (
            <p className={styles.muted} data-testid={`${testId}-vuoto`}>
              Nessuna modifica registrata.
            </p>
          )}
          {eventi && eventi.length > 0 && (
            <ol className={styles.list} data-testid={`${testId}-lista`}>
              {eventi.map((ev) => {
                const righe = auditChangeRows(ev, fieldLabel);
                return (
                  <li key={ev.eventId} data-testid={`${testId}-evento-${ev.eventId}`}>
                    <div className={styles.head}>
                      <time dateTime={ev.timestamp}>
                        {new Date(ev.timestamp).toLocaleString('it-IT')}
                      </time>
                      <strong>
                        {actionLabel
                          ? actionLabel(ev)
                          : (AUDIT_ACTION_LABEL[ev.action] ?? ev.action)}
                      </strong>
                      {describe && <span className={styles.subject}>{describe(ev)}</span>}
                      <span className={styles.actor}>{ev.actor}</span>
                    </div>
                    {righe.length > 0 && (
                      <ul className={styles.changes}>
                        {righe.map((r) => (
                          <li key={r.campo} data-field={r.campo}>
                            <span className={styles.field}>{r.label}:</span>{' '}
                            {r.prima !== null && !r.invariato ? (
                              <>
                                <del>{r.prima}</del> → {r.dopo}
                              </>
                            ) : (
                              r.dopo
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}
